import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Ban, Loader2 } from "lucide-react";

const CATEGORIES = [
  { value: "asset", label: "資産" },
  { value: "liability", label: "負債" },
  { value: "equity", label: "純資産" },
  { value: "revenue", label: "収益" },
  { value: "expense", label: "費用" },
  { value: "cost", label: "原価" },
];
const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

export default function Masters() {
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [years, setYears] = useState([]);
  const [balances, setBalances] = useState({});
  const [fyId, setFyId] = useState("");
  const [loading, setLoading] = useState(false);

  const [nCode, setNCode] = useState("");
  const [nName, setNName] = useState("");
  const [nCat, setNCat] = useState("expense");

  const [fyStart, setFyStart] = useState("2026-04-01");
  const [fyEnd, setFyEnd] = useState("2027-03-31");
  const [fyMonth, setFyMonth] = useState(3);

  const loadAccounts = useCallback(async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data);
  }, []);
  const loadYears = useCallback(async () => {
    const { data } = await api.get("/fiscal-years");
    setYears(data);
    if (data.length && !fyId) setFyId(data[0].id);
  }, [fyId]);
  const loadBalances = useCallback(async (yid) => {
    if (!yid) return;
    const { data } = await api.get(`/opening-balances?fiscal_year_id=${yid}`);
    const map = {};
    data.forEach((b) => { map[b.account_id] = { debit: b.debit, credit: b.credit }; });
    setBalances(map);
  }, []);

  useEffect(() => { loadAccounts(); loadYears(); }, [loadAccounts, loadYears]);
  useEffect(() => { if (tab === "balances" && fyId) loadBalances(fyId); }, [tab, fyId, loadBalances]);

  const seedDefaults = async () => {
    setLoading(true);
    try { await api.post("/accounts/seed-defaults"); toast.success("標準勘定科目を投入しました"); await loadAccounts(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const createAccount = async () => {
    if (!nCode || !nName) return;
    setLoading(true);
    try {
      await api.post("/accounts", { code: nCode, name: nName, category: nCat });
      toast.success("勘定科目を追加しました"); setNCode(""); setNName(""); await loadAccounts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const deactivate = async (id) => {
    try { await api.delete(`/accounts/${id}`); toast.success("使用停止にしました"); await loadAccounts(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createYear = async () => {
    setLoading(true);
    try {
      await api.post("/fiscal-years", { start_date: fyStart, end_date: fyEnd, closing_month: Number(fyMonth) });
      toast.success("会計年度を作成しました"); await loadYears();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const saveBalance = async (accountId) => {
    const b = balances[accountId] || { debit: 0, credit: 0 };
    try {
      await api.post("/opening-balances", { fiscal_year_id: fyId, account_id: accountId, debit: Number(b.debit) || 0, credit: Number(b.credit) || 0 });
      toast.success("保存しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const tabs = [
    { id: "accounts", label: "勘定科目" },
    { id: "years", label: "会計年度" },
    { id: "balances", label: "期首残高" },
  ];

  return (
    <div className="max-w-4xl" data-testid="masters-page">
      <h1 className="font-display font-bold text-3xl text-stone-900">会計マスター</h1>
      <p className="mt-2 text-sm text-stone-500">勘定科目・会計年度・期首残高を管理します。</p>

      <div className="mt-6 flex gap-2 border-b border-stone-200">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`masters-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-emerald-800 text-emerald-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "accounts" && (
        <div className="mt-6 space-y-5">
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="font-medium text-stone-900 flex items-center gap-2"><Plus className="h-4 w-4" />勘定科目を追加</div>
              <button onClick={seedDefaults} disabled={loading} data-testid="seed-accounts-button" className="text-sm text-emerald-800 hover:underline">標準勘定科目を投入</button>
            </div>
            <div className="mt-4 grid sm:grid-cols-4 gap-3">
              <div><Label className="text-sm">コード</Label><Input value={nCode} onChange={(e) => setNCode(e.target.value)} data-testid="account-code" className="mt-1.5" placeholder="710" /></div>
              <div className="sm:col-span-2"><Label className="text-sm">科目名</Label><Input value={nName} onChange={(e) => setNName(e.target.value)} data-testid="account-name" className="mt-1.5" placeholder="会議費" /></div>
              <div><Label className="text-sm">区分</Label>
                <select value={nCat} onChange={(e) => setNCat(e.target.value)} data-testid="account-category" className="mt-1.5 w-full rounded-md border border-stone-200 px-2 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <button onClick={createAccount} disabled={loading} data-testid="create-account-button" className="mt-4 inline-flex items-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "追加"}
            </button>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="accounts-list">
            {accounts.map((a) => (
              <div key={a.id} className={`flex items-center justify-between px-5 py-3 ${a.is_active ? "" : "opacity-50"}`}>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-stone-400 w-10">{a.code}</span>
                  <span className="text-sm text-stone-800">{a.name}</span>
                  <span className="text-xs rounded-full bg-stone-100 px-2 py-0.5 text-stone-600">{catLabel(a.category)}</span>
                  {!a.is_active && <span className="text-xs text-orange-600">使用停止</span>}
                </div>
                {a.is_active && (
                  <button onClick={() => deactivate(a.id)} data-testid={`deactivate-${a.code}`} className="text-stone-400 hover:text-orange-600" title="使用停止"><Ban className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "years" && (
        <div className="mt-6 space-y-5">
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <div className="font-medium text-stone-900">会計年度を作成</div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <div><Label className="text-sm">開始日</Label><Input type="date" value={fyStart} onChange={(e) => setFyStart(e.target.value)} data-testid="fy-start" className="mt-1.5" /></div>
              <div><Label className="text-sm">終了日</Label><Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} data-testid="fy-end" className="mt-1.5" /></div>
              <div><Label className="text-sm">決算月</Label><Input type="number" min={1} max={12} value={fyMonth} onChange={(e) => setFyMonth(e.target.value)} data-testid="fy-month" className="mt-1.5" /></div>
            </div>
            <button onClick={createYear} disabled={loading} data-testid="create-fy-button" className="mt-4 inline-flex items-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">作成</button>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="fy-list">
            {years.map((y) => (
              <div key={y.id} className="px-5 py-3 text-sm flex items-center justify-between">
                <span className="text-stone-800">{y.start_date} 〜 {y.end_date}（決算{y.closing_month}月）</span>
                <span className="text-xs rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">{y.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "balances" && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">会計年度</Label>
            <select value={fyId} onChange={(e) => setFyId(e.target.value)} data-testid="balance-fy-select" className="rounded-md border border-stone-200 px-2 py-1.5 text-sm">
              {years.map((y) => <option key={y.id} value={y.id}>{y.start_date}〜{y.end_date}</option>)}
            </select>
          </div>
          {!fyId && <p className="text-sm text-stone-500">先に会計年度を作成してください。</p>}
          {fyId && (
            <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="balances-list">
              {accounts.filter((a) => a.is_active).map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-5 py-2.5">
                  <span className="text-xs font-mono text-stone-400 w-10">{a.code}</span>
                  <span className="text-sm text-stone-800 flex-1 truncate">{a.name}</span>
                  <Input type="number" placeholder="借方" data-testid={`ob-debit-${a.code}`} value={balances[a.id]?.debit || ""} onChange={(e) => setBalances((p) => ({ ...p, [a.id]: { ...(p[a.id] || {}), debit: e.target.value } }))} className="w-28 h-9" />
                  <Input type="number" placeholder="貸方" data-testid={`ob-credit-${a.code}`} value={balances[a.id]?.credit || ""} onChange={(e) => setBalances((p) => ({ ...p, [a.id]: { ...(p[a.id] || {}), credit: e.target.value } }))} className="w-28 h-9" />
                  <button onClick={() => saveBalance(a.id)} data-testid={`ob-save-${a.code}`} className="text-xs rounded-md bg-emerald-900 px-3 py-1.5 text-white hover:bg-emerald-950">保存</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
